import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import FormInput from "app/components/Programs/FormInput";
import SelectBox from "app/components/OptionButtonGroup/SelectBox";
import StateDayPicker from "app/components/Programs/StateDayPicker";
import ButtonVariant from "app/components/Button/ButtonVariant";
import ButtonSimple from "app/components/Button/ButtonSimple";
import Label from "app/components/Programs/Label";
import { DateTime } from "luxon";
import styles from "./styles.module.scss";

interface CustomRecurrenceConfig {
  interval: number;
  frequency: "days" | "weeks";
  daysOfWeek: number[];
  startDate?: Date | null;
  untilDate?: Date | null;
  occurrences?: number | null;
}

interface CustomRecurrenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (_config: CustomRecurrenceConfig) => void;
  initialConfig?: CustomRecurrenceConfig | null;
  startTime?: Date | string | null;
}

// Convert a Date instance or ISO/date-only string to a JS Date in the local
// zone. Using `new Date("YYYY-MM-DD")` would parse as UTC and can land on the
// previous day in negative-offset time zones, so string values go through Luxon.
const toLocalDate = (value?: Date | string | null): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  return DateTime.fromISO(value.toString()).toJSDate();
};

const CustomRecurrenceModal: React.FC<CustomRecurrenceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialConfig,
  startTime,
}) => {
  const { t } = useTranslation();

  const methods = useForm({
    defaultValues: {
      interval: initialConfig?.interval?.toString() || "1",
      occurrences: initialConfig?.occurrences?.toString() || "",
    },
    mode: "onChange",
  });

  const { handleSubmit, formState, watch } = methods;
  const occurrencesValue = watch("occurrences");

  const frequencyOptions = [
    { value: "days", label: t("Day") },
    { value: "weeks", label: t("Week") },
  ];

  const daysOfWeekOptions = [
    { value: 0, label: t("Sun"), fullLabel: t("Sunday") },
    { value: 1, label: t("Mon"), fullLabel: t("Monday") },
    { value: 2, label: t("Tue"), fullLabel: t("Tuesday") },
    { value: 3, label: t("Wed"), fullLabel: t("Wednesday") },
    { value: 4, label: t("Thu"), fullLabel: t("Thursday") },
    { value: 5, label: t("Fri"), fullLabel: t("Friday") },
    { value: 6, label: t("Sat"), fullLabel: t("Saturday") },
  ];

  const [frequency, setFrequency] = useState<{ value: string; label: string }>(
    initialConfig?.frequency
      ? frequencyOptions.find((opt) => opt.value === initialConfig.frequency) || frequencyOptions[0]
      : frequencyOptions[0]
  );

  const [selectedDays, setSelectedDays] = useState<number[]>(
    initialConfig?.daysOfWeek || []
  );

  const [untilDate, setUntilDate] = useState<Date | null>(
    initialConfig?.untilDate || null
  );

  const [startDate, setStartDate] = useState<Date | null>(
    initialConfig?.startDate || toLocalDate(startTime)
  );

  const [endType, setEndType] = useState<"date" | "occurrences">(
    initialConfig?.occurrences ? "occurrences" : "date"
  );

  const [formErrors, setFormErrors] = useState({
    startDate: false,
    frequency: false,
    untilDate: false,
    occurrences: false,
  });

  const maxDate = useMemo(() => {
    const baseDate = startDate || toLocalDate(startTime);
    const base = baseDate ? DateTime.fromJSDate(baseDate) : null;
    if (!base) {
      return null;
    }

    if (frequency.value === "days") {
      return base.plus({ days: 52 }).toJSDate();
    } else {
      return base.plus({ years: 1 }).toJSDate();
    }
  }, [startDate, startTime, frequency]);

  useEffect(() => {
    if (isOpen) {
      if (initialConfig) {
        setFrequency(
          initialConfig.frequency
            ? frequencyOptions.find((opt) => opt.value === initialConfig.frequency) || frequencyOptions[0]
            : frequencyOptions[0]
        );
        setSelectedDays(initialConfig.daysOfWeek || []);
        setUntilDate(initialConfig.untilDate || null);
        setEndType(initialConfig.occurrences ? "occurrences" : "date");
        setStartDate(
          initialConfig.startDate || toLocalDate(startTime)
        );
      } else {
        // No saved config: reset every field to its default on each open so
        // values from a previous (e.g. cancelled) session don't leak in. The
        // component stays mounted, so nothing resets on its own. This also
        // resyncs the start date to the current program date.
        const startBase = toLocalDate(startTime);
        setFrequency(frequencyOptions[0]);
        setSelectedDays([]);
        setEndType("date");
        setFormErrors({ startDate: false, frequency: false, untilDate: false, occurrences: false });
        methods.reset({ interval: "1", occurrences: "" });
        setStartDate(startBase);
        // Default the end date to one month after the start date.
        setUntilDate(startBase ? DateTime.fromJSDate(startBase).plus({ months: 1 }).toJSDate() : null);
      }
    }
  }, [isOpen, initialConfig, startTime]);

  const toggleDay = (e: React.MouseEvent, dayValue: number) => {
    e.preventDefault();
    setSelectedDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue]
    );
  };

  // True when a chosen end date lands before the start date — an invalid
  // recurrence we must block (GeneralSettings skips this guard for custom repeats).
  const untilBeforeStart = useMemo(() => {
    if (endType !== "date" || !untilDate || !startDate) {
      return false;
    }
    return DateTime.fromJSDate(untilDate).startOf("day") < DateTime.fromJSDate(startDate).startOf("day");
  }, [endType, untilDate, startDate]);

  // True when the chosen end date is beyond the allowed window (maxDate). The
  // picker caps new selections, but moving the start date afterwards can leave
  // an already-picked end date out of range, so we re-validate it here.
  const untilAfterMax = useMemo(() => {
    if (endType !== "date" || !untilDate || !maxDate) {
      return false;
    }
    return DateTime.fromJSDate(untilDate).startOf("day") > DateTime.fromJSDate(maxDate).startOf("day");
  }, [endType, untilDate, maxDate]);

  const validateForm = () => {
    const occurrencesNum = occurrencesValue ? parseInt(occurrencesValue, 10) : 0;
    const errors = {
      startDate: !startDate,
      frequency: !frequency,
      untilDate: endType === "date" ? !untilDate : false,
      occurrences: endType === "occurrences" ? !occurrencesValue || occurrencesNum < 1 : false,
    };
    setFormErrors(errors);
    return !Object.values(errors).some(error => error) && !untilBeforeStart && !untilAfterMax;
  };

  const onSubmit = (data: any) => {
    if (!validateForm()) {
      return;
    }

    const config: CustomRecurrenceConfig = {
      interval: parseInt(data.interval, 10),
      frequency: frequency.value as "days" | "weeks",
      daysOfWeek: selectedDays,
      startDate,
    };

    if (endType === "date") {
      config.untilDate = untilDate;
    } else {
      config.occurrences = parseInt(data.occurrences, 10);
    }

    onSave(config);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <ModalLayout isOpen={isOpen} classNames={styles.modal} onClose={onClose}>
      <div className={styles.header} data-testid="custom-repeat-modal">
        <h2>{t("Custom Repeat")}</h2>
        <button
          type="button"
          className={styles.close_button}
          onClick={handleCancel}
          aria-label="Close"
        >
          <i className="ico ico-x" />
        </button>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.body}>
            <div className={styles.field}>
              <Label
                text={t("Start Date")}
                required={true}
                error={formErrors.startDate && t("This field is required")}
                item={
                  <StateDayPicker
                    onChange={(value: string) => {
                      setStartDate(toLocalDate(value));
                      setFormErrors((prev) => ({ ...prev, startDate: false }));
                    }}
                    date={startDate}
                    openToDate={startDate || new Date()}
                  />
                }
              />
              {startDate && (
                <p className={styles.first_session_text}>
                  {`${t("First session")}: ${DateTime.fromJSDate(startDate).toFormat("cccc, MMMM d, yyyy")}`}
                </p>
              )}
            </div>

            <div className={styles.row}>
              <div className={styles.field}>
                <FormInput
                  tagTitle={t("Repeat every")}
                  fieldName="interval"
                  placeholder="1"
                  isRequired={true}
                  onlyInteger={true}
                  hideLabel={false}
                  min={1}
                  max={52}
                />
              </div>
              <div className={styles.field}>
                <Label
                  text={null}
                  error={formErrors.frequency && t("This field is required")}
                  item={
                    <SelectBox
                      options={frequencyOptions}
                      value={frequency}
                      setValue={(value) => {
                        setFrequency(value);
                        setFormErrors((prev) => ({ ...prev, frequency: false }));
                      }}
                      customColor="#e6e6e6"
                    />
                  }
                />
              </div>
            </div>

            {frequency.value === "weeks" && (
              <div className={styles.days_of_week}>
                <Label
                  text={t("Repeat On")}
                  item={
                    <div className={styles.days_container}>
                      {daysOfWeekOptions.map((day) => (
                        <ButtonSimple
                          key={day.value}
                          buttonText={day.label}
                          onClick={(e) => toggleDay(e, day.value)}
                          isActive={selectedDays.includes(day.value)}
                          buttonStyle={styles.week_day_button}
                        />
                      ))}
                    </div>
                  }
                />
              </div>
            )}

            <div className={styles.end_options}>
              <Label
                text={t("Ends")}
                required={true}
                item={
                  <div className={styles.end_buttons}>
                    <ButtonSimple
                      buttonText={t("Date")}
                      onClick={(e) => {
                        e.preventDefault();
                        setEndType("date");
                        setFormErrors((prev) => ({ ...prev, occurrences: false }));
                      }}
                      isActive={endType === "date"}
                    />
                    <ButtonSimple
                      buttonText={t("Sessions")}
                      onClick={(e) => {
                        e.preventDefault();
                        setEndType("occurrences");
                        setFormErrors((prev) => ({ ...prev, untilDate: false }));
                      }}
                      isActive={endType === "occurrences"}
                    />
                  </div>
                }
              />
            </div>

            {endType === "date" && (
              <Label
                text={t("On")}
                required={true}
                error={
                  formErrors.untilDate
                    ? t("This field is required")
                    : untilBeforeStart
                      ? t("End date must be on or after the start date.")
                      : untilAfterMax
                        ? t("End date is beyond the maximum allowed range.")
                        : ""
                }
                item={
                  <StateDayPicker
                    onChange={(value: string) => {
                      setUntilDate(toLocalDate(value));
                      setFormErrors((prev) => ({ ...prev, untilDate: false }));
                    }}
                    date={untilDate}
                    openToDate={untilDate || new Date()}
                    maxDate={maxDate}
                  />
                }
              />
            )}

            {endType === "occurrences" && (
              <div className={`${styles.field} ${styles.after_input}`}>
                <div className={styles.occurrences_input_container}>
                  <FormInput
                    tagTitle={t("After")}
                    fieldName="occurrences"
                    placeholder="10"
                    isRequired={true}
                    onlyInteger={true}
                    hideLabel={false}
                    min={1}
                    max={52}
                  />
                  {!formState.errors.occurrences && (
                    <span className={styles.occurrences_text}>{t("sessions")}</span>
                  )}
                </div>
                <p className={styles.max_amount_text}>{t("Up to 52 sessions")}</p>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <ButtonVariant
              title={t("Cancel")}
              buttonType="secondary-filled"
              onClickFunc={handleCancel}
              data-testid="custom-repeat-cancel"
            />
            <ButtonVariant
              disabled={
                !formState.isValid ||
                !startDate ||
                !frequency ||
                (endType === "date" && (!untilDate || untilBeforeStart || untilAfterMax)) ||
                (endType === "occurrences" && (!occurrencesValue || parseInt(occurrencesValue, 10) < 1))
              }
              title={t("Save")}
              buttonType="primary-filled"
              onClickFunc={handleSubmit(onSubmit)}
              data-testid="custom-repeat-save"
            />
          </div>
        </form>
      </FormProvider>
    </ModalLayout>
  );
};

export default CustomRecurrenceModal;
